import { ChangeDetectorRef, Component, OnInit, inject, input } from '@angular/core';
import { Router } from '@angular/router';
import { AlertController, ModalController, PopoverController, ToastController, IonHeader, IonToolbar, IonTitle, IonButtons, IonButton, IonContent, IonIcon, IonSegment, IonSegmentButton, IonLabel, IonInput, IonSelect, IonSelectOption, IonTextarea, IonChip, IonCheckbox, IonFooter, IonSpinner, IonCard, IonModal } from '@ionic/angular/standalone';
import { firstValueFrom, Observable, Subscription } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { PaymentType, PaymentTypeKey, TaxIdType } from '../../../models/db-models';
import { AuthService } from '../../auth/auth.service';
import { Payee } from '../../../models/external-system.model';
import { UntypedFormBuilder, ValidatorFn, AbstractControl, ValidationErrors, Validators, FormArray, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { UntypedFormGroup } from '@angular/forms';
import { ConfigService } from '../../services/config.service';
import { cpf, cnpj } from 'cpf-cnpj-validator';
import { DbService } from '../../services/db.service';
import { PayeeService } from '../../services/payee.service';
import { connectFunctionsEmulator, getFunctions, httpsCallable } from '@angular/fire/functions';
import { environment } from '../../../environments/environment';
import { getApp } from '@angular/fire/app';
import { Timestamp } from '@angular/fire/firestore';
import { parseISO, format } from 'date-fns';
import { v4 as uuidv4 } from 'uuid';
import { NgClass, AsyncPipe, DatePipe, NgTemplateOutlet } from '@angular/common';
import { PayeesListComponent } from '../admin/payees-list/payees-list.component';
import { addIcons } from "ionicons";
import { isFirestoreIndexedDbError, requestFirestoreCacheRecovery } from '../../utils/firestore-cache.util';
import { close, alertCircleOutline, checkmarkCircleOutline, personOutline, shieldCheckmarkOutline, peopleOutline, linkOutline, shieldOutline, informationCircleOutline, cashOutline, timeOutline, closeCircleOutline, mailOutline, personAddOutline, personRemoveOutline, addOutline, trashOutline, musicalNotesOutline, saveOutline, banOutline, lockOpenOutline, documentTextOutline, personCircleOutline } from "ionicons/icons";

interface ComposerGroup {
  id?: string;
  companyName?: string;
  taxId?: string;
  isNew?: boolean;
  totalStatementSum?: number;
}

@Component({
  selector: 'app-payee-info',
  templateUrl: './payee-info.component.html',
  styleUrls: ['./payee-info.component.scss'],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    NgClass,
    NgTemplateOutlet,
    PayeesListComponent,
    AsyncPipe,
    DatePipe,
    IonHeader,
    IonToolbar,
    IonTitle,
    IonButtons,
    IonButton,
    IonContent,
    IonIcon,
    IonSegment,
    IonSegmentButton,
    IonLabel,
    IonInput,
    IonSelect,
    IonSelectOption,
    IonTextarea,
    IonChip,
    IonCheckbox,
    IonFooter,
    IonSpinner,
    IonModal,
  ],
  standalone: true,
})
export class PayeeInfoComponent implements OnInit {
  auth = inject(AuthService);
  private modalController = inject(ModalController);
  private popoverController = inject(PopoverController);
  private alertController = inject(AlertController);
  private toastController = inject(ToastController);
  private fb = inject(UntypedFormBuilder);
  private cdr = inject(ChangeDetectorRef);
  configService = inject(ConfigService);
  db = inject(DbService);
  private payeeService = inject(PayeeService);
  private router = inject(Router);


  readonly payeeInput = input<Payee | null>(null, { alias: 'payee' });
  payee: Payee;
  readonly showClose = input(false);
  readonly paymentOnly = input(false);
  readonly initialSegment = input<string | null>(null);
  parentPayee: Payee;
  payeeInfoForm: UntypedFormGroup;
  isAdmin$: Observable<boolean>;
  isSuperadmin$: Observable<boolean>;
  paymentType = PaymentType;
  selectedSegment = 'register';
  taxIdTypes = Object.values(TaxIdType);
  errorMessage: string;
  requesting = false;
  successMessage: string;
  showSuccessMessage = false;
  parentPayeeSubscription: Subscription;
  payeeSubscription: Subscription;

  // Composer conversion modal state
  isConvertModalOpen = false;
  convertCountdown = 10;
  private convertComposerId: string | null = null;
  private convertComposerName: string | null = null;
  convertEmail: string | null = null;
  private convertIntervalId: any;
  presentingElementEl: HTMLElement | null = null;

  get form() {
    return this.payeeInfoForm.controls;
  }

  get formErrorClasses() {
    return {
      'ion-invalid': this.payeeInfoForm.invalid || (this.payee && this.payeeInfoForm.invalid),
      'ion-touched': this.payeeInfoForm.touched || (this.payee && this.payeeInfoForm.invalid),
    };
  }

  get phone1InputErrorClasses() {
    const ctrl = this.payeeInfoForm?.get('internalProperties.phone1');
    if (!ctrl) {
      return {};
    }
    const showErrorState = ctrl.touched || (!!this.payee?.id && ctrl.invalid);
    return {
      'ion-invalid': ctrl.invalid && showErrorState,
      'ion-touched': showErrorState,
    };
  }

  constructor() {
    addIcons({close,alertCircleOutline,personOutline,shieldCheckmarkOutline,peopleOutline,linkOutline,cashOutline,shieldOutline,informationCircleOutline,checkmarkCircleOutline,timeOutline,closeCircleOutline,mailOutline,documentTextOutline,lockOpenOutline,banOutline,personAddOutline,personRemoveOutline,addOutline,trashOutline,musicalNotesOutline,saveOutline,personCircleOutline});
  }

  ngOnInit() {
    this.payee = this.payeeInput() as any;
    this.isAdmin$ = this.auth.customClaims$.pipe(
      map(customClaims => this.auth.hasAnyCustomClaims(customClaims, ['manager', 'finance']))
    );
    this.isSuperadmin$ = this.auth.customClaims$.pipe(
      map(customClaims => !!customClaims?.['superadmin'])
    );
    const initial = this.initialSegment();
    if (initial) {
      this.selectedSegment = initial;
    }
    this.initForm();
    this.getParentPayee();
    // Only set defaults if not in payment-only mode (they're not needed for payment-only editing)
    if (!this.paymentOnly()) {
      this.setDefaultContractId();
      this.setDefaultShare();
    }
    this.subscribeToPayee();
    this.setPresentingElementForNestedModal();
  }

  ngOnDestroy() {
    this.parentPayeeSubscription?.unsubscribe();
    this.payeeSubscription?.unsubscribe();
    if (this.convertIntervalId) {
      clearInterval(this.convertIntervalId);
    }
  }

  getTimestamp(timestamp: Timestamp | null) {
    if (!timestamp) return null;
    return new Timestamp(timestamp.seconds, timestamp.nanoseconds).toDate();
  }

  private getInputDateString(ts?: Timestamp | null): string {
    if (!ts) return '';
    const d = new Timestamp(ts.seconds, ts.nanoseconds).toDate();
    return format(d, 'yyyy-MM-dd');
  }

  private normalizeInternalProperties(internalProperties: any): any {
    if (!internalProperties) return {};
    const birthDateValue = internalProperties.birthDate;
    return {
      ...internalProperties,
      birthDate: birthDateValue ? Timestamp.fromDate(parseISO(birthDateValue)) : null,
    };
  }

  private removeUndefinedValues<T extends Record<string, any>>(obj: T): Partial<T> {
    const cleaned: Partial<T> = {};
    for (const key in obj) {
      let value = obj[key];
      
      // Convert NaN to null for numbers
      if (typeof value === 'number' && isNaN(value)) {
        value = null;
      }
      
      // Skip undefined values
      if (value !== undefined) {
        // Recursively clean nested objects
        if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
          if ((value as any) instanceof Timestamp) {
            cleaned[key] = value;
          } else {
            cleaned[key] = this.removeUndefinedValues(value as Record<string, any>) as any;
          }
        } else if (Array.isArray(value)) {
          // Clean arrays by recursively cleaning objects and converting NaN to null
          cleaned[key] = value.map(item => {
            if (typeof item === 'number' && isNaN(item)) {
              return null;
            }
            if (item !== null && typeof item === 'object' && !Array.isArray(item)) {
              if ((item as any) instanceof Timestamp) {
                return item;
              }
              return this.removeUndefinedValues(item as Record<string, any>);
            }
            return item;
          }) as any;
        } else {
          cleaned[key] = value;
        }
      }
    }
    return cleaned;
  }

  subscribeToPayee() {
    if (this.payee?.id) {
      this.payeeSubscription?.unsubscribe();
      this.payeeSubscription = this.db.getPayee(this.payee.id).subscribe((payee) => {
        this.payee = payee;
      });
    };
  }

  getParentPayee() {
    if (this.payee?.internalProperties?.parentPayee) {
      this.parentPayeeSubscription = this.db.getPayee(this.payee.internalProperties.parentPayee).subscribe((parentPayee) => {
        this.parentPayee = parentPayee;
      });
    }
  }

  async setDefaultContractId() {
    if (!this.payee?.id) {
      const contractsConfig = await firstValueFrom(this.configService.contractsConfig$);
      const defaultContractId = contractsConfig.defaultId;
      this.payeeInfoForm.get('internalProperties.contract.clickId')?.setValue(defaultContractId);
    } else {
      // Only set to empty string if there's no existing clickId value
      if (!this.payee?.internalProperties?.contract?.clickId) {
        this.payeeInfoForm.get('internalProperties.contract.clickId')?.setValue('');
      }
    }
  }

  async setDefaultShare() {
    const shareValue = this.payeeInfoForm.get('internalProperties.share')?.value;
    if (!this.payee?.id || (shareValue === null || shareValue === undefined || shareValue === '')) {
      const contractsConfig = await firstValueFrom(this.configService.contractsConfig$);
      const defaultShare = contractsConfig.defaultShare;
      if (defaultShare !== undefined && defaultShare !== null) {
        this.payeeInfoForm.get('internalProperties.share')?.setValue(defaultShare);
      }
    }
  }

  private normalizeTypeKey(typeKey: string | undefined): string | undefined {
    if (typeKey === 'CPF/CNPJ') {
      return PaymentTypeKey.CPF_CNPJ;
    }
    return typeKey;
  }

  initForm() {
    const taxIdValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
      const taxIdType = this.payeeInfoForm?.get('internalProperties.taxIdType')?.value || this.payee?.internalProperties?.taxIdType;
      const value = control.value;

      if (!value) return null;

      switch (taxIdType) {
        case TaxIdType.CPF:
          return cpf.isValid(value) ? null : { invalidCpf: true };
        case TaxIdType.CNPJ:
          return cnpj.isValid(value) ? null : { invalidCnpj: true };
        case TaxIdType.OTHER:
          return null;
        default:
          return null;
      }
    };

    const bankTaxIdValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
      const type = this.payeeInfoForm?.get('paymentInfo.type')?.value || this.payee?.paymentInfo?.type;
      const value = control.value;
      if (!value) return null;

      const typeKey = this.payeeInfoForm?.get('paymentInfo.typeKey')?.value;
      const normalizedTypeKey = typeKey === 'CPF/CNPJ' ? PaymentTypeKey.CPF_CNPJ : typeKey;

      if (type === PaymentType.TED
        || (type === PaymentType.PIX && normalizedTypeKey === PaymentTypeKey.CPF_CNPJ)
      ) {
        return cpf.isValid(value) || cnpj.isValid(value) ? null : { invalidCpfOrCnpj: true };
      }
      if (type === PaymentType.PIX && normalizedTypeKey === PaymentTypeKey.EMAIL) {
        return Validators.email(control);
      }
      if (type === PaymentType.PIX && normalizedTypeKey === PaymentTypeKey.PHONE) {
        return Validators.pattern('^[0-9]{10,12}$')(control);
      }
      return null;
    };

    this.payeeInfoForm = this.fb.group({
      id: [this.payee?.id || ''],
      companyName: [{
        value: this.payee?.companyName,
        disabled: false,
      }, this.paymentOnly() ? [] : [Validators.required]],
      paymentInfo: this.fb.group({
        bankNumber: [this.payee?.paymentInfo?.bankNumber],
        bankName: [this.payee?.paymentInfo?.bankName],
        agency: [this.payee?.paymentInfo?.agency],
        account: [this.payee?.paymentInfo?.account],
        type: [this.payee?.paymentInfo?.type || PaymentType.PIX],
        typeKey: [this.normalizeTypeKey(this.payee?.paymentInfo?.typeKey)],
        typeValue: [this.payee?.paymentInfo?.typeValue, [bankTaxIdValidator]],
      }),
      internalProperties: this.fb.group({
        email: [{
          value: this.payee?.internalProperties?.email,
          disabled: !!this.payee?.internalProperties?.invitedAt,
        }, this.paymentOnly() ? [] : [Validators.email]],
        phone1: [this.payee?.internalProperties?.phone1 ?? '', [Validators.maxLength(15)]],
        taxId: [this.payee?.internalProperties?.taxId, this.paymentOnly() ? [] : [taxIdValidator]],
        taxIdType: [this.payee?.internalProperties?.taxIdType || TaxIdType.CPF],
        birthDate: [this.getInputDateString(this.payee?.internalProperties?.birthDate)],
        share: [this.payee?.internalProperties?.share],
        notes: [this.payee?.internalProperties?.notes],
        contract: this.fb.group({
          clickId: [this.payee?.internalProperties?.contract?.clickId],
        }),
        linkedPayees: [this.payee?.internalProperties?.linkedPayees || []],
        parentPayee: [this.payee?.internalProperties?.parentPayee || ''],
        publishingOnly: [this.payee?.internalProperties?.publishingOnly || false],
        composers: this.fb.array([]),
      })
    });

    // Require email and share when creating a new payee (and not in payment-only mode)
    if (!this.payee?.id && !this.paymentOnly()) {
      const emailCtrl = this.payeeInfoForm.get('internalProperties.email');
      const shareCtrl = this.payeeInfoForm.get('internalProperties.share');
      emailCtrl?.setValidators([Validators.required, Validators.email]);
      shareCtrl?.setValidators([Validators.required]);
      emailCtrl?.updateValueAndValidity();
      shareCtrl?.updateValueAndValidity();
    }

    // Subscribe to taxIdType changes to revalidate taxId
    this.payeeInfoForm.get('internalProperties.taxIdType')?.valueChanges.subscribe(() => {
      this.payeeInfoForm.get('internalProperties.taxId')?.updateValueAndValidity();
    });

    this.payeeInfoForm.get('paymentInfo.type')?.valueChanges.subscribe(() => {
      this.onTypeKeyChange();
    });

    this.payeeInfoForm.get('paymentInfo.typeKey')?.valueChanges.subscribe(() => {
      this.payeeInfoForm.get('paymentInfo.typeValue')?.updateValueAndValidity();
    });

    this.onTypeKeyChange();

    this.payee?.internalProperties?.composers?.forEach((composer) => {
      this.getComposersArray().push(this.createComposerGroup({
        id: composer.id,
        companyName: composer.companyName,
        taxId: composer.taxId,
        isNew: false,
        totalStatementSum: composer.totalStatementSum,
      }));
    });

    // TODO: DEBUG - Can be removed after debugging
    // this.payeeInfoForm.valueChanges.subscribe(() => {
    //   this.debugFormState();
    // });
  }

  getFormatErrorText() {
    if (this.payeeInfoForm.get('internalProperties.taxId')?.errors?.invalidCpf) {
      return 'CPF inválido';
    }
    if (this.payeeInfoForm.get('internalProperties.taxId')?.errors?.invalidCnpj) {
      return 'CNPJ inválido';
    }
    return '';
  }

  getEmailErrorText() {
    const ctrl = this.payeeInfoForm.get('internalProperties.email');
    if (ctrl?.hasError('required')) return 'Campo obrigatório';
    if (ctrl?.hasError('email')) return 'E-mail inválido';
    return '';
  }

  getPhone1ErrorText() {
    const ctrl = this.payeeInfoForm.get('internalProperties.phone1');
    if (ctrl?.hasError('maxlength')) return 'Máximo de 15 caracteres';
    return '';
  }

  getShareErrorText() {
    const ctrl = this.payeeInfoForm.get('internalProperties.share');
    if (ctrl?.hasError('required')) return 'Campo obrigatório';
    return '';
  }

  getBankFormatErrorText() {
    if (this.payeeInfoForm.get('paymentInfo.typeValue')?.errors?.invalidCpfOrCnpj) {
      return 'CPF ou CNPJ inválido';
    }
    if (this.payeeInfoForm.get('paymentInfo.typeValue')?.errors?.email) {
      return 'E-mail inválido';
    }
    if (this.payeeInfoForm.get('paymentInfo.typeValue')?.errors?.pattern) {
      return 'Telefone inválido';
    }
    return '';
  }

  onTypeKeyChange() {
    const paymentType = this.payeeInfoForm.get('paymentInfo.type')?.value;
    const currentTypeKey = this.payeeInfoForm.get('paymentInfo.typeKey')?.value;
    
    // Only set default value if there's no existing value
    if (!currentTypeKey) {
      const typeKey = paymentType === PaymentType.PIX ? PaymentTypeKey.CPF_CNPJ : '';
      this.payeeInfoForm.get('paymentInfo.typeKey')?.setValue(typeKey);
    }

    this.cdr.detectChanges();
  }

  selectSegment(event: any) {
    this.selectedSegment = event.detail.value;
  }

  async save() {
    this.requesting = true;
    this.errorMessage = '';
    let highlightedId: string | null = null;

    try {
      // Normalize typeKey before saving to ensure we always save CPF_CNPJ instead of CPF/CNPJ
      const formValue = { ...this.payeeInfoForm.value };
      if (formValue.paymentInfo?.typeKey === 'CPF/CNPJ') {
        formValue.paymentInfo.typeKey = PaymentTypeKey.CPF_CNPJ;
      }

      if (!this.payee?.id) {
        // Check if another payee with this email already exists
        const newEmail = this.payeeInfoForm.get('internalProperties.email')?.value;
        if (newEmail) {
          const existingPayee = await this.db.getPayeeByEmailOnce(newEmail);
          if (existingPayee) {
            this.errorMessage = 'Este e-mail já está associado a outro Beneficiário';
            this.requesting = false;
            return;
          }
        }
        
        highlightedId = await this.createExternalPayee();
        // After creating, save the rest of the info and close if in payment-only mode
        await this.db.savePayee({
          ...this.payee,
          ...formValue,
          internalProperties: {
            ...this.payee?.internalProperties,
            ...this.removeUndefinedValues(this.normalizeInternalProperties(formValue?.internalProperties)),
            userUpdatedAt: Timestamp.now()
          }
        }, highlightedId || this.payee?.id);
        this.payeeInfoForm.markAsPristine();
        await this.presentToast(this.paymentOnly() ? 'Informações salvas com sucesso' : 'Beneficiário salvo com sucesso');
        this.dismiss(true, highlightedId || this.payee?.id || undefined);
      } else {
        // Check if email has changed and if another payee with that email exists
        const newEmail = this.payeeInfoForm.get('internalProperties.email')?.value;
        const currentEmail = this.payee?.internalProperties?.email;
        
        if (newEmail && newEmail !== currentEmail) {
          const existingPayee = await this.db.getPayeeByEmailOnce(newEmail, this.payee.id);
          if (existingPayee) {
            this.errorMessage = 'Este e-mail já está associado a outro Beneficiário';
            this.requesting = false;
            return;
          }
        }
        
        if (this.payee?.companyName !== this.payeeInfoForm.get('companyName')?.value) {
          await this.updateExternalPayee();
        }

        const customClaims = await firstValueFrom(this.auth.customClaims$);
        const canSaveFullObject = this.auth.hasAnyCustomClaims(customClaims, ['manager', 'finance']);
        const hasNoClaims = this.auth.hasNoCustomClaims(customClaims);

        let payeeToSave: Partial<Payee>;
        
        if (canSaveFullObject) {
          // Save entire object if user has manager or finance claims
          payeeToSave = {
            ...this.payee,
            ...formValue,
            internalProperties: {
              ...this.payee?.internalProperties,
              ...this.removeUndefinedValues(this.normalizeInternalProperties(formValue?.internalProperties)),
              userUpdatedAt: Timestamp.now()
            }
          };
        } else if (hasNoClaims) {
          // Save only paymentInfo if user has no custom claims
          payeeToSave = {
            paymentInfo: formValue.paymentInfo
          };
        }

        await this.db.savePayee(payeeToSave, this.payee.id);
        
        // Only save parent payees if user has manager/finance claims
        if (canSaveFullObject) {
          await this.saveParentPayees();
        }
        highlightedId = this.payee.id;

        this.payeeInfoForm.markAsPristine();
        await this.presentToast(this.paymentOnly() ? 'Informações salvas com sucesso' : 'Beneficiário salvo com sucesso');
        this.dismiss(true, highlightedId || this.payee?.id || undefined);
      }
    } catch (error) {
      this.errorMessage = this.errorMessage || `Erro ao salvar beneficiário: ${error.message}`;
    } finally {
      this.requesting = false;
      // Don't close the modal after saving - just mark form as pristine
      // The form is already marked as pristine above, so no additional action needed
    }
  }

  private async createExternalPayee(overrides?: Partial<Payee>): Promise<string | null> {
    try {
      const functions = getFunctions(getApp(), environment.defaultRegion);
      if (environment.useFunctionsEmulator) {
        connectFunctionsEmulator(functions, 'localhost', 6001);
      }
      const createRevelatorPayee = httpsCallable<{ payee: Payee }, { id: string }>(functions, 'createRevelatorPayee');
      const formId = this.payeeInfoForm.get('id')?.value?.trim();
      const formValue = { ...this.payeeInfoForm.value };
      // Normalize typeKey before sending to external system
      if (formValue.paymentInfo?.typeKey === 'CPF/CNPJ') {
        formValue.paymentInfo.typeKey = PaymentTypeKey.CPF_CNPJ;
      }
      const res = await createRevelatorPayee({
        payee: {
          ...formValue,
          ...(overrides || {}),
          id: (overrides as any)?.id ?? (formId || this.payee?.id || undefined)
        },
      });
      const createdId = (res?.data as any)?.id || null;
      if (createdId) {
        // Start listening to updates for the new payee id
        this.payee = { ...(this.payee || {} as any), id: createdId } as any;
        this.subscribeToPayee();
      }
      return createdId;
    } catch (error: any) {
      console.log('Error when creating payee', error);
      if (error?.code === 'functions/already-exists') {
        this.errorMessage = 'Este e-mail já está associado a outro Beneficiário';
      } else {
        this.errorMessage = 'Falha ao criar beneficiário externo';
      }
      throw error;
    }
  }

  private async updateExternalPayee() {
    const functions = getFunctions(getApp(), environment.defaultRegion);
    if (environment.useFunctionsEmulator) {
      connectFunctionsEmulator(functions, 'localhost', 6001);
    }
    const updateExternalPayee = httpsCallable<{ payee: Payee }, { id: string }>(functions, 'updateExternalPayee');
    const formValue = { ...this.payeeInfoForm.value };
    // Normalize typeKey before sending to external system
    if (formValue.paymentInfo?.typeKey === 'CPF/CNPJ') {
      formValue.paymentInfo.typeKey = PaymentTypeKey.CPF_CNPJ;
    }

    await updateExternalPayee({
      payee: {
        ...formValue,
        id: this.payee?.id
      },
    });
  }

  private async saveParentPayees() {
    const linkedPayeesIds = this.payeeInfoForm.get('internalProperties.linkedPayees')?.value || [];
    if (linkedPayeesIds.length > 0) {
      linkedPayeesIds.forEach(async (linkedPayeeId) => {
        const linkedPayee = await this.db.getPayeeOnceWithId(linkedPayeeId) as any;
        if (linkedPayee) {
          linkedPayee.internalProperties = {
            ...linkedPayee.internalProperties,
            parentPayee: this.payee.id
          };
          await this.db.savePayee(linkedPayee, linkedPayee.id);
        }
      });
    }

    const currentChildrenPayees = await this.db.getRevelatorPayeeByParentPayeeOnce(this.payee.id);
    currentChildrenPayees.forEach(async (childPayee) => {
      if (childPayee.internalProperties?.parentPayee && !linkedPayeesIds?.includes(childPayee?.id)) {
        childPayee.internalProperties = {
          ...childPayee.internalProperties,
          parentPayee: null
        };
        await this.db.savePayee(childPayee, childPayee.id);
      }
    });
  }

  async sendInvitation() {
    this.requesting = true;
    this.errorMessage = '';

    try {
      const currentUser = await firstValueFrom(this.auth.user$);
      const currentUserEmail = currentUser?.email || null;

      const cleanedPayeeData = {
        ...this.payee,
        ...this.payeeInfoForm.value,
        internalProperties: {
          ...this.payee?.internalProperties,
          ...this.removeUndefinedValues(this.normalizeInternalProperties(this.payeeInfoForm.value?.internalProperties)),
          invitedBy: currentUserEmail,
          userUpdatedAt: Timestamp.now()
        }
      };
      
      await this.db.savePayee(cleanedPayeeData, this.payee.id);
      
      const functions = getFunctions(getApp(), environment.defaultRegion);
      if (environment.useFunctionsEmulator) {
        connectFunctionsEmulator(functions, 'localhost', 6001);
      }
      const inviteUser = httpsCallable<{ payee: Payee, id: string }, { invitedAt: Timestamp }>(functions, 'inviteUser');
      const result = await inviteUser({
        payee: this.removeUndefinedValues(cleanedPayeeData) as Payee,
        id: this.payee.id
      });

      this.payeeInfoForm.markAsPristine();
      this.payeeInfoForm.get('internalProperties.email')?.disable();

    } catch (error) {
      if (requestFirestoreCacheRecovery(error)) {
        return;
      }

      this.errorMessage = isFirestoreIndexedDbError(error)
        ? 'Erro de cache local do navegador. Recarregue a página e tente novamente.'
        : `Erro ao enviar convite: ${error instanceof Error ? error.message : String(error)}`;
    } finally {
      this.requesting = false;
    }
  }

  async removeAccess() {
    this.requesting = true;
    this.errorMessage = '';

    try {
      const currentUser = await firstValueFrom(this.auth.user$);
      const currentUserEmail = currentUser?.email || null;

      await this.db.savePayee(
        {
          ...this.payee,
          ...this.payeeInfoForm.value,
          internalProperties: {
            ...this.payee?.internalProperties,
            ...this.removeUndefinedValues(this.normalizeInternalProperties(this.payeeInfoForm.value?.internalProperties)),
            removedBy: currentUserEmail,
            userUpdatedAt: Timestamp.now()
          }
        }, this.payee.id);

      const functions = getFunctions(getApp(), environment.defaultRegion);
      if (environment.useFunctionsEmulator) {
        connectFunctionsEmulator(functions, 'localhost', 6001);
      }
      const removeUser = httpsCallable<{ id: string }, { id: string }>(functions, 'removeUser');
      await removeUser({
        id: this.payee.id
      });

      this.payeeInfoForm.markAsPristine();
      this.payeeInfoForm.get('internalProperties.email')?.enable();

      this.requesting = false;
    } catch (error) {
      this.errorMessage = `Erro ao remover acesso: ${error.message}`;
    } finally {
      this.requesting = false;
    }
  }

  async blockPayee() {
    const alert = await this.alertController.create({
      header: 'Confirmar bloqueio',
      message: 'Você tem certeza que deseja bloquear este Beneficiário?',
      buttons: [
        {
          text: 'Cancelar',
          role: 'cancel'
        },
        {
          text: 'SIM',
          role: 'confirm',
          handler: async () => {
            if (!this.payee?.id) {
              this.errorMessage = 'Não é possível bloquear um beneficiário sem ID';
              return;
            }

            this.requesting = true;
            this.errorMessage = '';

            try {
              // Set blocked property
              if (!this.payee.internalProperties) {
                this.payee.internalProperties = {};
              }
              this.payee.internalProperties.blocked = true;

              // If payee has invitedAt, remove access first
              if (this.payee.internalProperties.invitedAt) {
                await this.db.savePayee(
                  {
                    ...this.payee,
                    ...this.payeeInfoForm.value,
                    internalProperties: {
                      ...this.payee?.internalProperties,
                      ...this.removeUndefinedValues(this.normalizeInternalProperties(this.payeeInfoForm.value?.internalProperties)),
                      userUpdatedAt: Timestamp.now()
                    }
                  }, this.payee.id);

                const functions = getFunctions(getApp(), environment.defaultRegion);
                if (environment.useFunctionsEmulator) {
                  connectFunctionsEmulator(functions, 'localhost', 6001);
                }
                const removeUser = httpsCallable<{ id: string }, { id: string }>(functions, 'removeUser');
                await removeUser({
                  id: this.payee.id
                });

                this.payeeInfoForm.get('internalProperties.email')?.enable();
              }

              // Save the payee with blocked status
              await this.db.savePayee({
                ...this.payee,
                ...this.payeeInfoForm.value,
                internalProperties: {
                  ...this.payee.internalProperties,
                  ...this.removeUndefinedValues(this.normalizeInternalProperties(this.payeeInfoForm.value?.internalProperties)),
                  blocked: true,
                  userUpdatedAt: Timestamp.now()
                }
              }, this.payee.id);

              this.payeeInfoForm.markAsPristine();
              await this.presentToast('Beneficiário bloqueado com sucesso');
            } catch (error) {
              this.errorMessage = `Erro ao bloquear beneficiário: ${error.message}`;
            } finally {
              this.requesting = false;
            }
          }
        }
      ]
    });

    await alert.present();
  }

  async unblockPayee() {
    if (!this.payee?.id) {
      this.errorMessage = 'Não é possível desbloquear um beneficiário sem ID';
      return;
    }

    this.requesting = true;
    this.errorMessage = '';

    try {
      // Set blocked property to false
      if (!this.payee.internalProperties) {
        this.payee.internalProperties = {};
      }
      this.payee.internalProperties.blocked = false;

      // Save the payee with unblocked status
      await this.db.savePayee({
        ...this.payee,
        ...this.payeeInfoForm.value,
        internalProperties: {
          ...this.payee.internalProperties,
          ...this.removeUndefinedValues(this.normalizeInternalProperties(this.payeeInfoForm.value?.internalProperties)),
          blocked: false,
          userUpdatedAt: Timestamp.now()
        }
      }, this.payee.id);

      this.payeeInfoForm.markAsDirty();
      await this.presentToast('Beneficiário desbloqueado com sucesso');
    } catch (error) {
      this.errorMessage = `Erro ao desbloquear beneficiário: ${error.message}`;
    } finally {
      this.requesting = false;
    }
  }

  async selectAsCurrentPayee() {
    if (!this.payee?.id) return;
    this.payeeService.selectPayeeId(this.payee.id);
    this.router.navigate(['/pages/earnings']);
    try {
      await this.popoverController.dismiss();
    } catch {}
    try {
      await this.modalController.dismiss();
    } catch {}
  }

  async dismiss(refresh = false, highlightedId?: string) {
    if (!refresh && this.payeeInfoForm.dirty) {
      // Alternative approach using AlertController
      const alert = await this.alertController.create({
        header: 'Atenção',
        message: 'Você fez alterações. Deseja mesmo fechar?',
        buttons: [
          {
            text: 'Cancelar',
            role: 'cancel',
            handler: () => {
            },
          },
          {
            text: 'OK',
            role: 'confirm',
            handler: () => {
              this.modalController.dismiss({ refresh: false });
            },
          },
        ],
      });

      await alert.present();
      return;
    }
    // Try to dismiss popover if this component is inside a popover
    try {
      await this.popoverController.dismiss({ refresh, highlightedId });
      return;
    } catch (e) {}
    // Fallback to modal dismissal
    try {
      await this.modalController.dismiss({ refresh, highlightedId });
    } catch (e) {}
  }

  onPayeeSelected(id: string | null) {
    if (id) {
      const linkedPayees = this.payeeInfoForm.get('internalProperties.linkedPayees')?.value || [];
      const index = linkedPayees.indexOf(id);
      if (index === -1) {
        linkedPayees.push(id);
      } else {
        linkedPayees.splice(index, 1);
      }
      this.payeeInfoForm.get('internalProperties.linkedPayees')?.setValue(linkedPayees);
      this.payeeInfoForm.markAsDirty();
    }
  }

  getComposersArray() {
    return this.payeeInfoForm.get('internalProperties.composers') as FormArray;
  }

  private sortedComposers: any[] = [];
  private lastComposersLength = 0;
  private lastComposersHash = '';

  getSortedComposers() {
    const composersArray = this.getComposersArray();
    if (!composersArray || !composersArray.controls) {
      return [];
    }

    // Create a hash to detect changes efficiently (include isNew status)
    const currentHash = composersArray.controls.map(control =>
      `${control.get('companyName')?.value || ''}:${control.get('isNew')?.value}`
    ).join('|');

    // Only re-sort if the array length or content changed
    if (this.lastComposersLength !== composersArray.controls.length || this.lastComposersHash !== currentHash) {
      // Create an array of objects with index and control for sorting
      const composersWithIndex = composersArray.controls.map((control, index) => ({
        control,
        index,
        companyName: control.get('companyName')?.value || '',
        isNew: control.get('isNew')?.value || false
      }));

      // Separate new composers from existing ones (sort alphabetically)
      const newComposers = composersWithIndex.filter(c => c.isNew);
      const existingComposers = composersWithIndex.filter(c => !c.isNew);

      // Sort only existing composers by company name (case insensitive)
      existingComposers.sort((a, b) => {
        const nameA = a.companyName.toLowerCase();
        const nameB = b.companyName.toLowerCase();
        return nameA.localeCompare(nameB);
      });

      // New composers at top (reversed so most recent is first), followed by sorted existing ones
      this.sortedComposers = [...newComposers.reverse(), ...existingComposers];
      this.lastComposersLength = composersArray.controls.length;
      this.lastComposersHash = currentHash;
    }

    return this.sortedComposers;
  }

  trackComposer(index: number, item: { control: UntypedFormGroup }): string {
    return item?.control?.get('id')?.value || String(index);
  }

  createComposerGroup({
    id = '',
    companyName = '',
    taxId = '',
    isNew = true,
    totalStatementSum = 0
  }: ComposerGroup = {}): UntypedFormGroup {
    if (!id) {
      id = uuidv4();
    }
    return this.fb.group({
      id: [id],
      companyName: [companyName, Validators.required],
      taxId: [taxId],
      isNew: [isNew],
      totalStatementSum: [totalStatementSum],
    });
  }

  removeComposer(index: number) {
    const composers = this.getComposersArray();
    composers.removeAt(index);
    this.payeeInfoForm.markAsDirty();
  }

  addComposer() {
    const composers = this.getComposersArray();
    composers.push(this.createComposerGroup());
    this.payeeInfoForm.markAsDirty();
  }

  async convertComposerToBeneficiary(composerId: string, composerName: string) {
    this.convertComposerId = composerId;
    this.convertComposerName = composerName;
    this.convertEmail = null;
    this.isConvertModalOpen = true;
    this.startConvertCountdown();
  }

  private startConvertCountdown() {
    if (this.convertIntervalId) {
      clearInterval(this.convertIntervalId);
    }
    this.convertCountdown = 10;
    this.convertIntervalId = setInterval(() => {
      this.convertCountdown = this.convertCountdown - 1;
      if (this.convertCountdown <= 0) {
        clearInterval(this.convertIntervalId);
        this.convertIntervalId = null;
        this.convertCountdown = 0;
        this.cdr.detectChanges();
      }
    }, 1000);
  }

  private async setPresentingElementForNestedModal() {
    try {
      const topOverlay = await this.modalController.getTop();
      this.presentingElementEl = (topOverlay as any)?.el || null;
    } catch (e) {
      this.presentingElementEl = null;
    }
  }

  closeConvertComposerModal() {
    this.isConvertModalOpen = false;
    if (this.convertIntervalId) {
      clearInterval(this.convertIntervalId);
      this.convertIntervalId = null;
    }
    this.convertEmail = null;
  }

  onConvertModalDismiss() {
    if (this.convertIntervalId) {
      clearInterval(this.convertIntervalId);
      this.convertIntervalId = null;
    }
  }

  async confirmConvertComposer() {
    if (this.requesting) return;
    if (!this.convertComposerId || !this.convertComposerName) {
      this.closeConvertComposerModal();
      return;
    }
    if (!this.isValidEmail(this.convertEmail || '')) {
      await this.presentToast('E-mail inválido');
      return;
    }

    try {
      this.requesting = true;

      // 1) Capture parent id BEFORE we switch context to the new payee
      const parentId = this.payee?.id as string;

      // Create external payee using composer ID, name and provided email
      const parentShare = (this.payee?.internalProperties as any)?.share
        ?? this.payeeInfoForm.get('internalProperties.share')?.value
        ?? null;

      const createdId = await this.createExternalPayee({
        id: this.convertComposerId,
        companyName: this.convertComposerName,
        isPortalPayee: false,
        paymentInfo: null,
        internalProperties: {
          email: this.convertEmail!,
          share: parentShare,
          internalStatementsSum: 0,
          internalStatementsSumIndividual: 0,
          internalPublishingSum: 0,
          internalPublishingSumIndividual: 0,
          externalBalanceSum: 0,
          externalBalanceIndividual: 0,
          totalBalance: 0,
          lastBalance: 0,
          hasPublishingAccess: true,
        }
      } as any);

      if (!createdId) {
        throw new Error('Falha ao criar beneficiário externo');
      }

      // 2) Transactionally: set up new payee doc and remove composer from parent
      await this.db.convertComposerToPayee(parentId, createdId, {
        companyName: this.convertComposerName,
      } as any);

      // 3) Load the newly created payee so admin can edit it
      const newPayee = await this.db.getPayeeOnceWithId(createdId);
      if (newPayee) {
        this.payee = newPayee as any;
      }
      this.subscribeToPayee();
      this.initForm();
      this.setDefaultContractId();
      this.selectedSegment = 'register';
      this.cdr.detectChanges();
      this.payeeInfoForm.markAsPristine();
      await this.presentToast('Compositor convertido em beneficiário com sucesso');
    } catch (e: any) {
      this.errorMessage = e?.message || 'Erro ao converter compositor';
    } finally {
      this.requesting = false;
      this.closeConvertComposerModal();
    }
  }

  isValidEmail(email: string | null): boolean {
    if (!email) return false;
    const re = /[^\s@]+@[^\s@]+\.[^\s@]+/;
    return re.test(email);
  }

  getFormErrors(): string {
    if (this.payeeInfoForm.valid) {
      return '';
    }

    const fieldLabels: { [key: string]: string } = {
      'companyName': 'Nome',
      'internalProperties.email': 'E-mail',
      'internalProperties.phone1': 'Telefone',
      'internalProperties.taxId': 'CPF / CNPJ',
      'internalProperties.share': '% Share',
      'paymentInfo.typeValue': this.getPaymentTypeValueLabel(),
    };

    const errorFields: string[] = [];

    const checkControl = (path: string, label: string) => {
      const control = this.payeeInfoForm.get(path);
      if (control && control.invalid) {
        errorFields.push(label);
      }
    };

    Object.keys(fieldLabels).forEach(path => {
      checkControl(path, fieldLabels[path]);
    });

    if (errorFields.length === 0) {
      return '';
    }

    if (errorFields.length === 1) {
      return `Existem erros no campo: ${errorFields[0]}`;
    }

    const lastField = errorFields.pop();
    return `Existem erros nos campos: ${errorFields.join(', ')} e ${lastField}`;
  }

  private getPaymentTypeValueLabel(): string {
    const paymentType = this.payeeInfoForm.get('paymentInfo.type')?.value;
    const typeKey = this.payeeInfoForm.get('paymentInfo.typeKey')?.value;
    
    if (paymentType === PaymentType.TED) {
      return 'CPF / CNPJ do titular';
    }
    
    if (paymentType === PaymentType.PIX) {
      const normalizedTypeKey = typeKey === 'CPF/CNPJ' ? PaymentTypeKey.CPF_CNPJ : typeKey;
      
      switch (normalizedTypeKey) {
        case PaymentTypeKey.CPF_CNPJ:
          return 'Chave PIX';
        case PaymentTypeKey.EMAIL:
          return 'E-mail PIX';
        case PaymentTypeKey.PHONE:
          return 'Telefone PIX';
        default:
          return 'Chave PIX';
      }
    }
    
    return 'Conta de pagamento';
  }

  private async presentToast(message: string) {
    const toast = await this.toastController.create({
      message,
      duration: 1500,
      position: 'top'
    });
    await toast.present();
  }
}
